/**
 * Skeleton Loaders for Atlas Casino
 * Premium loading states with shimmer animations
 */

import { theme } from '../constants/theme.js';

// Shimmer animation CSS
const shimmerStyles = `
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
  .skeleton-shimmer {
    background: linear-gradient(
      90deg,
      rgba(255,255,255,0.03) 0%,
      rgba(255,255,255,0.08) 50%,
      rgba(255,255,255,0.03) 100%
    );
    background-size: 200% 100%;
    animation: shimmer 1.5s ease-in-out infinite;
  }
`;

/**
 * Base Skeleton Block
 */
export function SkeletonBlock({ width, height, rounded = 'lg', className = '' }) {
  return (
    <div
      className={`skeleton-shimmer ${className}`}
      style={{
        width,
        height,
        borderRadius: rounded === 'full' ? '9999px' : rounded === 'xl' ? '16px' : rounded === 'lg' ? '12px' : '8px',
        backgroundColor: 'rgba(255,255,255,0.05)',
      }}
    />
  );
}

/**
 * Game Card Skeleton - for game lobby
 */
export function GameCardSkeleton() {
  return (
    <>
      <style>{shimmerStyles}</style>
      <div
        className="rounded-2xl overflow-hidden"
        style={{
          backgroundColor: theme.card,
          border: `1px solid ${theme.border}`,
        }}
      >
        {/* Image placeholder */}
        <div className="aspect-[4/3] relative overflow-hidden">
          <div
            className="skeleton-shimmer absolute inset-0"
            style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
          />
          {/* Badges */}
          <div className="absolute top-3 left-3 flex gap-2">
            <SkeletonBlock width={40} height={20} rounded="full" />
            <SkeletonBlock width={36} height={20} rounded="full" />
          </div>
          {/* Favorite button */}
          <div className="absolute top-3 right-3">
            <SkeletonBlock width={32} height={32} rounded="full" />
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          <SkeletonBlock width="70%" height={20} className="mb-2" />
          <div className="flex justify-between items-center">
            <SkeletonBlock width={60} height={16} />
            <SkeletonBlock width={50} height={16} />
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Game Lobby Skeleton - full lobby loading state
 */
export function GameLobbySkeleton() {
  return (
    <div
      className="h-dvh flex flex-col overflow-hidden"
      style={{ backgroundColor: theme.bg, fontFamily: theme.fontPrimary }}
    >
      <style>{shimmerStyles}</style>

      {/* Header */}
      <header
        className="flex items-center justify-between px-6 py-4"
        style={{
          background: 'rgba(0,0,0,0.3)',
          borderBottom: `1px solid ${theme.border}`,
        }}
      >
        <div className="flex items-center gap-4">
          <SkeletonBlock width={40} height={40} rounded="xl" />
          <SkeletonBlock width={120} height={24} />
        </div>
        <SkeletonBlock width={100} height={40} rounded="xl" />
      </header>

      {/* Search & Filters */}
      <div className="px-6 py-4">
        <SkeletonBlock width="100%" height={48} rounded="xl" className="mb-4" />
        <div className="flex gap-3 overflow-x-auto pb-2">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <SkeletonBlock key={i} width={100} height={40} rounded="full" />
          ))}
        </div>
      </div>

      {/* Game Grid */}
      <div className="px-6 py-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
            <GameCardSkeleton key={i} />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Game Loading State - for individual games
 */
export function GameLoadingSkeleton({ gameName = 'Game' }) {
  return (
    <div
      className="h-dvh flex flex-col overflow-hidden"
      style={{ backgroundColor: theme.bg, fontFamily: theme.fontPrimary }}
    >
      <style>{shimmerStyles}</style>

      {/* Header */}
      <header
        className="flex items-center justify-between px-6 py-4"
        style={{
          background: 'linear-gradient(135deg, rgba(123,47,247,0.15) 0%, rgba(88,28,135,0.1) 100%)',
          borderBottom: `1px solid rgba(123,47,247,0.2)`,
        }}
      >
        <div className="flex items-center gap-4">
          <SkeletonBlock width={44} height={44} rounded="xl" />
          <div>
            <SkeletonBlock width={150} height={24} className="mb-2" />
            <SkeletonBlock width={200} height={14} />
          </div>
        </div>
        <SkeletonBlock width={120} height={44} rounded="xl" />
      </header>

      {/* Main game area */}
      <main className="flex-1 flex flex-col items-center justify-center p-6">
        {/* Central game element placeholder */}
        <div className="mb-8">
          <div
            className="w-56 h-56 rounded-full skeleton-shimmer flex items-center justify-center"
            style={{ backgroundColor: 'rgba(123,47,247,0.1)' }}
          >
            <div className="text-center">
              <div
                className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center text-white text-2xl font-bold"
                style={{ background: theme.grad, boxShadow: theme.purpleGlow }}
              >
                <span className="animate-pulse">A</span>
              </div>
              <div
                className="w-8 h-8 mx-auto border-4 border-t-transparent rounded-full animate-spin"
                style={{ borderColor: theme.purple, borderTopColor: 'transparent' }}
              />
            </div>
          </div>
        </div>

        {/* Controls placeholder */}
        <div className="w-full max-w-lg space-y-4">
          <SkeletonBlock width="100%" height={60} rounded="xl" />
          <SkeletonBlock width="100%" height={100} rounded="xl" />
          <SkeletonBlock width="100%" height={56} rounded="xl" />
        </div>
      </main>

      {/* Footer */}
      <footer
        className="px-6 py-4"
        style={{
          background: 'rgba(0,0,0,0.3)',
          borderTop: `1px solid ${theme.border}`,
        }}
      >
        <div className="flex items-center justify-center gap-6">
          <SkeletonBlock width={80} height={28} rounded="full" />
          <SkeletonBlock width={100} height={28} rounded="full" />
          <SkeletonBlock width={60} height={28} rounded="full" />
        </div>
      </footer>
    </div>
  );
}

/**
 * Dashboard Skeleton
 */
export function DashboardSkeleton() {
  return (
    <div
      className="h-dvh flex overflow-hidden"
      style={{ backgroundColor: theme.bg, fontFamily: theme.fontPrimary }}
    >
      <style>{shimmerStyles}</style>

      {/* Sidebar */}
      <aside
        className="w-64 p-4"
        style={{
          background: 'rgba(0,0,0,0.3)',
          borderRight: `1px solid ${theme.border}`,
        }}
      >
        <SkeletonBlock width="100%" height={48} rounded="xl" className="mb-6" />
        {[1, 2, 3, 4, 5].map((i) => (
          <SkeletonBlock key={i} width="100%" height={44} rounded="lg" className="mb-2" />
        ))}
      </aside>

      {/* Main content */}
      <main className="flex-1 p-6">
        <SkeletonBlock width={200} height={32} className="mb-6" />
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[1, 2, 3].map((i) => (
            <SkeletonBlock key={i} width="100%" height={120} rounded="xl" />
          ))}
        </div>
        <SkeletonBlock width="100%" height={300} rounded="xl" />
      </main>
    </div>
  );
}

/**
 * Button Loading State
 */
export function ButtonLoading({ children, loading, ...props }) {
  return (
    <button {...props} disabled={loading || props.disabled}>
      {loading ? (
        <div className="flex items-center justify-center gap-2">
          <div
            className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: 'currentColor', borderTopColor: 'transparent' }}
          />
          <span>Loading...</span>
        </div>
      ) : (
        children
      )}
    </button>
  );
}

/**
 * Inline Spinner
 */
export function Spinner({ size = 20, color = theme.purple }) {
  return (
    <div
      className="border-2 border-t-transparent rounded-full animate-spin"
      style={{
        width: size,
        height: size,
        borderColor: color,
        borderTopColor: 'transparent',
      }}
    />
  );
}

const SkeletonLoaders = {
  SkeletonBlock,
  GameCardSkeleton,
  GameLobbySkeleton,
  GameLoadingSkeleton,
  DashboardSkeleton,
  ButtonLoading,
  Spinner,
};

export default SkeletonLoaders;
